<template>
  <header class="flex items-center gap-2 bg-gray-200 px-2.5 py-2.5">
    <button type="button" class="rounded-full p-1 text-gray-700 hover:bg-gray-300" title="Retour" @click="emit('back')">
      <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" /></svg>
    </button>
    <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">IA</span>
    <span class="min-w-0 flex-1">
      <span class="block truncate text-sm font-bold text-gray-900">Assistant IA</span>
      <span class="block truncate text-[11px] text-gray-600">{{ model || 'Modèle non configuré' }}</span>
    </span>
    <button v-if="canView" type="button" class="rounded-full p-1.5 text-gray-600 hover:bg-gray-300" :class="{ 'bg-gray-300': showHistory }" title="Historique des conversations" @click="toggleHistory">
      <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
    </button>
    <button v-if="canView" type="button" class="rounded-full p-1.5 text-gray-600 hover:bg-gray-300" title="Nouvelle conversation" :disabled="sending" @click="newConversation">
      <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>
    </button>
    <button v-if="canView && conversationId" type="button" class="rounded-full p-1.5 text-gray-600 hover:bg-gray-300" title="Supprimer la conversation" :disabled="sending" @click="removeConversation">
      <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.87 12.14A2 2 0 0116.14 21H7.86a2 2 0 01-1.99-1.86L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3M4 7h16" /></svg>
    </button>
    <button type="button" class="rounded-full p-1.5 text-gray-600 hover:bg-gray-300" :title="props.expanded ? 'Réduire' : 'Agrandir'" @click="emit('toggle-expand')">
      <svg v-if="!props.expanded" class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>
      <svg v-else class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 9L4 4m5 5V4m0 5H4m11 0l5-5m-5 5V4m0 5h5M9 15l-5 5m5-5v5m0-5H4m11 0l5 5m-5-5v5m0-5h5" /></svg>
    </button>
    <button type="button" class="rounded-full p-1.5 text-gray-600 hover:bg-gray-300" title="Fermer" @click="emit('close')">
      <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
    </button>
  </header>

  <div v-if="showHistory" class="flex-1 overflow-y-auto bg-white px-2 py-2">
    <p class="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Mes conversations</p>
    <p v-if="conversationsLoading" class="p-4 text-center text-sm text-gray-500">Chargement…</p>
    <p v-else-if="!conversationsList.length" class="p-4 text-center text-sm text-gray-500">Aucune conversation.</p>
    <div v-else class="flex flex-col gap-1">
      <button
        v-for="c in conversationsList"
        :key="c.id"
        type="button"
        class="flex items-start justify-between gap-2 rounded-xl px-3 py-2 text-left hover:bg-gray-100"
        :class="c.id === conversationId ? 'bg-blue-50' : ''"
        @click="openConversation(c.id)"
      >
        <span class="min-w-0 flex-1">
          <span class="block truncate text-sm font-medium text-gray-900">{{ c.title }}</span>
          <span class="block text-[11px] text-gray-500">{{ c.messageCount }} message{{ c.messageCount > 1 ? 's' : '' }} · {{ formatHistoryDate(c.lastMessageAt || c.updatedAt) }}</span>
        </span>
        <span
          role="button"
          class="shrink-0 rounded-full p-1 text-gray-400 hover:bg-red-100 hover:text-red-600"
          title="Supprimer"
          @click.stop="deleteConversationFromList(c.id)"
        >
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </span>
      </button>
    </div>
  </div>

  <div v-else ref="scrollEl" class="flex-1 space-y-2 overflow-y-auto bg-white px-3 py-3">
    <p v-if="!messages.length && !pending" class="p-6 text-center text-sm text-gray-500">Aucun message.</p>
    <div v-for="m in messages" :key="m.id" class="flex" :class="m.role === 'USER' ? 'justify-end' : 'justify-start'">
      <div
        v-if="m.role === 'USER'"
        class="max-w-[85%] rounded-2xl bg-blue-600 px-3 py-2 text-sm text-white flex flex-col gap-1"
      >
        <div v-if="m.content" class="md-user-bubble"><MarkdownContent :content="m.content" inverted /></div>
        <div v-if="m.attachments && m.attachments.length" class="flex flex-wrap gap-1">
          <img
            v-for="(att, i) in m.attachments"
            :key="i"
            :src="att.url"
            :alt="att.name || 'image'"
            class="max-h-48 max-w-[200px] rounded-lg border border-white/30 object-cover"
          />
        </div>
      </div>
      <div
        v-else
        class="w-full max-w-full break-words rounded-2xl bg-gray-200 px-3 py-2 text-sm text-gray-900 flex flex-col gap-1"
      >
        <div v-if="m.toolCalls && m.toolCalls.length" class="flex flex-wrap gap-1">
          <span
            v-for="(tc, i) in m.toolCalls"
            :key="i"
            class="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium"
            :class="tc.ok ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-red-200 bg-red-50 text-red-700'"
          >{{ tc.ok ? '🔧' : '⚠️' }} {{ tc.label }}</span>
        </div>
        <MarkdownContent :content="m.content" />
        <div v-if="m.attachments && m.attachments.length" class="flex flex-wrap gap-1">
          <img
            v-for="(att, i) in m.attachments"
            :key="i"
            :src="att.url"
            :alt="att.name || 'image'"
            class="max-h-64 max-w-[260px] rounded-lg border border-gray-300 object-contain bg-white"
          />
        </div>
        <button
          v-if="m.content"
          type="button"
          class="flex w-fit items-center gap-1 self-start rounded px-1.5 py-0.5 text-[10px] text-gray-500 hover:bg-gray-300 hover:text-gray-800"
          :title="copiedMessageId === m.id ? 'Copié !' : 'Copier toute la réponse'"
          @click="copyFullMessage(m)"
        >
          <svg class="h-3 w-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
          {{ copiedMessageId === m.id ? 'Copié !' : 'Copier la réponse' }}
        </button>
      </div>
    </div>
    <div v-if="pending" class="flex justify-start">
      <div class="flex max-w-[85%] flex-col gap-1">
        <div v-if="liveTools.length" class="flex flex-col gap-1">
          <span
            v-for="t in liveTools"
            :key="t.id"
            class="inline-flex items-center gap-1.5 self-start rounded-full border px-2 py-1 text-xs font-medium"
            :class="t.status === 'started' ? 'border-blue-200 bg-blue-50 text-blue-700' : t.status === 'error' ? 'border-red-200 bg-red-50 text-red-700' : 'border-green-200 bg-green-50 text-green-700'"
          >
            <span v-if="t.status === 'started'" class="inline-block h-2 w-2 animate-pulse rounded-full bg-blue-500"></span>
            <span v-else>{{ t.status === 'error' ? '⚠️' : '✓' }}</span>
            {{ t.label }}
          </span>
        </div>
        <div class="flex items-center gap-1.5">
          <div class="w-2 h-2 rounded-full bg-blue-500" style="animation: aiBounce 1.4s infinite ease-in-out both; animation-delay: 0s;"></div>
          <div class="w-2 h-2 rounded-full bg-blue-500" style="animation: aiBounce 1.4s infinite ease-in-out both; animation-delay: 0.2s;"></div>
          <div class="w-2 h-2 rounded-full bg-blue-500" style="animation: aiBounce 1.4s infinite ease-in-out both; animation-delay: 0.4s;"></div>
        </div>
      </div>
    </div>
    <div v-if="errorText" class="flex justify-start">
      <div class="max-w-[85%] break-words rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{{ errorText }}</div>
    </div>
  </div>

  <form v-if="canView && !showHistory" class="border-t border-gray-200 bg-white p-2.5" @submit.prevent="send">
    <div class="flex flex-col gap-2 rounded-3xl border border-gray-300 bg-gray-50 px-3.5 py-2.5 shadow-sm transition-colors focus-within:border-blue-400 focus-within:bg-white">
      <div v-if="attachments.length" class="flex flex-wrap gap-2">
        <div
          v-for="(att, i) in attachments"
          :key="i"
          class="group relative h-14 w-14 shrink-0"
        >
          <div class="h-full w-full overflow-hidden rounded-2xl border border-gray-200 bg-white">
            <img :src="att.url" :alt="att.name" class="h-full w-full object-cover" />
          </div>
          <button
            type="button"
            class="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-gray-800 text-white ring-2 ring-white shadow-md hover:bg-black"
            title="Retirer"
            @click="removeAttachment(i)"
          >
            <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      </div>
      <textarea
        v-model="draft"
        rows="1"
        maxlength="8000"
        placeholder="Écrire à l'assistant…"
        class="max-h-28 min-h-[1.5rem] w-full resize-none border-0 bg-transparent px-0.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
        @keydown.enter.exact.prevent="send"
      />
      <div class="flex items-center justify-between">
        <button
          type="button"
          class="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-200 disabled:opacity-50"
          :disabled="sending"
          title="Joindre une image"
          @click="fileInput?.click()"
        >
          <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>
        </button>
        <input
          ref="fileInput"
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          multiple
          @change="onFilesSelected"
          class="hidden"
        />
        <button
          type="submit"
          class="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-white transition-colors hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400"
          :disabled="sending || (!draft.trim() && attachments.length === 0)"
          title="Envoyer"
        >
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 19V5m-7 7l7-7 7 7" /></svg>
        </button>
      </div>
    </div>
  </form>
  <p v-else-if="!showHistory" class="border-t border-gray-300 bg-gray-100 p-2 text-center text-xs text-gray-500">Lecture seule.</p>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { apolloClient } from '@/lib/apollo'
import MarkdownContent from '@/components/MarkdownContent.vue'
import { showConfirm, showToast, copyToClipboard } from '@/lib/ui'
import { useAuthStore } from '@/stores/auth'
import {
  AI_ASSISTANT_MODEL_QUERY, AI_CONVERSATIONS_QUERY, AI_MESSAGES_QUERY,
  CREATE_AI_CONVERSATION, DELETE_AI_CONVERSATION, SEND_AI_MESSAGE,
} from '@/graphql/aiAssistant'

const emit = defineEmits<{ (e: 'back'): void; (e: 'close'): void; (e: 'toggle-expand'): void }>()
const props = defineProps<{ expanded?: boolean }>()

const authStore = useAuthStore()
const canView = authStore.isAdmin || Boolean(authStore.user?.aiAssistantAccess)
const BACKEND_URL = import.meta.env.VITE_GRAPHQL_URL?.replace('/graphql', '') || 'http://localhost:4000'

const model = ref<string | null>(null)
const conversationId = ref<number | null>(null)
const messages = ref<any[]>([])
const draft = ref('')
const sending = ref(false)
const pending = ref(false)
const errorText = ref('')
const scrollEl = ref<HTMLElement | null>(null)
const fileInput = ref<HTMLInputElement | null>(null)
const attachments = ref<Array<{ url: string; name: string }>>([])

// Copier la reponse complete d'un message (hors tableaux/blocs de code qui ont
// leur propre bouton dans MarkdownContent) : utile quand on veut tout reutiliser.
const copiedMessageId = ref<number | null>(null)
const copyFullMessage = async (m: any) => {
  const ok = await copyToClipboard(m.content ?? '')
  if (!ok) {
    showToast('error', 'Impossible de copier (presse-papiers indisponible)')
    return
  }
  copiedMessageId.value = m.id
  setTimeout(() => { if (copiedMessageId.value === m.id) copiedMessageId.value = null }, 1500)
}

// Historique : liste de toutes les conversations de l'utilisateur (l'ancienne
// ne disparait jamais, elle reste accessible ici apres "Nouvelle conversation").
const showHistory = ref(false)
const conversationsList = ref<any[]>([])
const conversationsLoading = ref(false)

const loadConversationsList = async () => {
  conversationsLoading.value = true
  try {
    const { data }: any = await apolloClient.query({ query: AI_CONVERSATIONS_QUERY, fetchPolicy: 'network-only' })
    conversationsList.value = data.aiConversations ?? []
  } catch (e) {
    console.error(e)
    showToast('error', "Impossible de charger l'historique des conversations")
  } finally {
    conversationsLoading.value = false
  }
}

const toggleHistory = async () => {
  showHistory.value = !showHistory.value
  if (showHistory.value) await loadConversationsList()
}

const openConversation = async (id: number) => {
  showHistory.value = false
  if (id === conversationId.value) return
  conversationId.value = id
  errorText.value = ''
  draft.value = ''
  attachments.value = []
  await loadMessages(id)
}

const deleteConversationFromList = async (id: number) => {
  if (!(await showConfirm({ title: 'Supprimer la conversation', message: 'Supprimer cette conversation ?', variant: 'danger', confirmText: 'Supprimer' }))) return
  try {
    await apolloClient.mutate({ mutation: DELETE_AI_CONVERSATION, variables: { id } })
    conversationsList.value = conversationsList.value.filter((c) => c.id !== id)
    if (id === conversationId.value) newConversation()
  } catch (e) {
    console.error(e)
    showToast('error', 'Impossible de supprimer la conversation')
  }
}

const formatHistoryDate = (iso: string | null) => {
  if (!iso) return ''
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

// Outils en cours d'execution pendant la requete en vol : affiches a la place
// d'une simple attente, pour que l'utilisateur voie ce que l'IA fait.
let liveToolSeq = 0
const liveTools = ref<Array<{ id: number; name: string; label: string; status: 'started' | 'done' | 'error' }>>([])
let toolSocket: WebSocket | null = null

const connectToolSocket = () => {
  if (!canView || !authStore.token || toolSocket) return
  const wsUrl = BACKEND_URL.replace(/^http/, 'ws') + `/ws/notifications?token=${encodeURIComponent(authStore.token)}`
  const ws = new WebSocket(wsUrl)
  ws.onmessage = (event) => {
    let payload: any = null
    try { payload = JSON.parse(event.data) } catch { return }
    if (payload?.type !== 'ai.tool') return
    // Pendant un envoi en cours, conversationId.value peut etre encore null
    // (nouvelle conversation pas encore connue cote client) : on accepte alors
    // tout evenement, le serveur ne traite qu'une requete a la fois pour ce fil.
    if (conversationId.value != null && payload.conversationId != null && payload.conversationId !== conversationId.value) return
    if (payload.status === 'started') {
      liveTools.value = [...liveTools.value, { id: ++liveToolSeq, name: payload.name, label: payload.label, status: 'started' }]
      return
    }
    // Met a jour la derniere entree « en cours » correspondant a ce nom/libelle.
    for (let i = liveTools.value.length - 1; i >= 0; i--) {
      if (liveTools.value[i].name === payload.name && liveTools.value[i].label === payload.label && liveTools.value[i].status === 'started') {
        liveTools.value = liveTools.value.map((t, idx) => (idx === i ? { ...t, status: payload.status } : t))
        break
      }
    }
  }
  ws.onclose = () => { if (toolSocket === ws) toolSocket = null }
  toolSocket = ws
}

onBeforeUnmount(() => { toolSocket?.close(); toolSocket = null })

const scrollDown = () => nextTick(() => { if (scrollEl.value) scrollEl.value.scrollTop = scrollEl.value.scrollHeight })

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = reject
  })

const onFilesSelected = async (event: Event) => {
  const input = event.target as HTMLInputElement
  const files = input.files
  if (!files || files.length === 0) return
  for (const file of Array.from(files)) {
    if (!file.type.startsWith('image/')) continue
    if (file.size > 8 * 1024 * 1024) {
      showToast('error', `${file.name} dépasse 8 Mo et ne peut être jointe.`)
      continue
    }
    try {
      const url = await fileToBase64(file)
      attachments.value.push({ url, name: file.name })
    } catch {
      showToast('error', `Impossible de lire ${file.name}.`)
    }
  }
  input.value = ''
}

const removeAttachment = (index: number) => {
  attachments.value.splice(index, 1)
}

const loadMessages = async (id: number) => {
  const { data }: any = await apolloClient.query({ query: AI_MESSAGES_QUERY, variables: { conversationId: id }, fetchPolicy: 'network-only' })
  // Copie : les resultats Apollo sont geles et non extensibles.
  messages.value = [...(data.aiMessages ?? [])]
  await scrollDown()
}

// Reprend la conversation la plus recente ; sinon etat vide (aucun message automatique).
onMounted(async () => {
  connectToolSocket()
  try {
    const m: any = await apolloClient.query({ query: AI_ASSISTANT_MODEL_QUERY, fetchPolicy: 'network-only' })
    model.value = m.data.aiAssistantModel
    const { data }: any = await apolloClient.query({ query: AI_CONVERSATIONS_QUERY, fetchPolicy: 'network-only' })
    const last = data.aiConversations[0]
    if (last) { conversationId.value = last.id; await loadMessages(last.id) }
  } catch (e) {
    console.error(e)
    errorText.value = "Impossible de charger l'assistant."
  }
})

const newConversation = () => {
  conversationId.value = null
  messages.value = []
  errorText.value = ''
  draft.value = ''
  attachments.value = []
}

const removeConversation = async () => {
  if (!conversationId.value) return
  if (!(await showConfirm({ title: 'Supprimer la conversation', message: 'Supprimer cette conversation ?', variant: 'danger', confirmText: 'Supprimer' }))) return
  try {
    await apolloClient.mutate({ mutation: DELETE_AI_CONVERSATION, variables: { id: conversationId.value } })
    newConversation()
  } catch (e) {
    console.error(e)
    showToast('error', 'Impossible de supprimer la conversation')
  }
}

const send = async () => {
  const content = draft.value.trim()
  if ((!content && attachments.value.length === 0) || sending.value) return
  sending.value = true
  errorText.value = ''
  const atts = [...attachments.value]
  draft.value = ''
  attachments.value = []
  // Bulle utilisateur immediate, remplacee par la version persistee a la reponse.
  const tempId = -Date.now()
  messages.value.push({ id: tempId, role: 'USER', content, attachments: atts.map(a => ({ url: a.url })) })
  pending.value = true
  liveTools.value = []
  await scrollDown()
  try {
    const { data }: any = await apolloClient.mutate({ mutation: SEND_AI_MESSAGE, variables: { conversationId: conversationId.value, content, attachments: atts.map(a => a.url) } })
    const r = data.sendAiMessage
    messages.value = messages.value.filter((m) => m.id !== tempId)
    messages.value.push(r.userMessage)
    if (r.assistantMessage) messages.value.push(r.assistantMessage)
    if (!conversationId.value) {
      const list: any = await apolloClient.query({ query: AI_CONVERSATIONS_QUERY, fetchPolicy: 'network-only' })
      conversationId.value = list.data.aiConversations[0]?.id ?? null
    }
    if (!r.ok) errorText.value = r.error || "L'assistant n'a pas pu répondre."
  } catch (e: any) {
    messages.value = messages.value.filter((m) => m.id !== tempId)
    draft.value = content
    attachments.value = atts
    errorText.value = e?.graphQLErrors?.[0]?.message || e?.message || "L'assistant n'a pas pu répondre."
  } finally {
    pending.value = false
    sending.value = false
    liveTools.value = []
    await scrollDown()
  }
}

// Ouvre (ou cree) une conversation precise depuis l'exterieur du panneau
// (ex. bouton "Discuter avec l'IA" d'une notification) : si un message
// d'amorce est fourni, il est envoye immediatement avec tout le contexte
// deja prepare cote serveur (voir startAiChatFromNotification).
const openWithSeed = async (convId: number | null, seed: string) => {
  showHistory.value = false
  if (convId) {
    conversationId.value = convId
    errorText.value = ''
    draft.value = ''
    attachments.value = []
    await loadMessages(convId)
  } else {
    newConversation()
  }
  if (seed) {
    draft.value = seed
    await send()
  }
}

defineExpose({ openWithSeed })

</script>

<style scoped>
@keyframes aiBounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}
</style>
